import type { RecordedJourneyType } from '@featit/shared/project-progress';
import type { GuestRoleKeyType, IGuestFilingForm, IGuestFilingPrefill } from '@featit/shared';
import type { IGuestQuestionAnswersInput } from '@featit/shared/questions';
import {
  clarityCallerWaitMs,
  type IClarityAnswer,
  type IClarityFiling,
  type IClarityStepRequest,
} from '@featit/shared/clarity-check';
import { ApiService } from './ApiService';
import { expectList, expectRecord } from './answerShape';
import { workAreaApis } from '@/data-sources/WorkAreaApiRegistry';

/**
 * The body the server sends a guest for a shared item (T-0786, AGM-19): this allowlist and nothing
 * else — no description and no internal status name; `label` is where it stands in the team's words
 * for the asker.
 */
export interface IGuestItemBody {
  id: string;
  key: string;
  title: string;
  label: string;
  fields: Record<string, Record<string, unknown>>;
  /** Only the comments sent to the reporter — never an internal one. */
  messages: Array<{
    id: string;
    authorName: string | null;
    content: string;
    createdAt: string;
    /** D-0429 (open): present only where the work area tells customers a message began as a draft. */
    aiDrafted?: { by: string | null };
  }>;
  /** The questions put to a guest role the reader reads the item through (AGM-19). */
  questions: IGuestQuestion[];
  /**
   * F-0692 AGM-66: the description as the team shared it, the newest version; or, before any share,
   * what the reader filed (AGM-64). Absent while the reader may read none.
   */
  content?: string;
  /** Which shared version `content` is; null for what the reader filed. */
  contentVersion?: number | null;
  /** F-0779 PX-51: what the reader wrote and answered at filing; absent unless they filed it. */
  filing?: IClarityFiling;
  /**
   * PX-5 (I-2286): when each step of the customer's journey was first reached, by the flow nodes
   * that name one; absent where none does, and from an older server.
   */
  journey?: RecordedJourneyType;
  /**
   * I-2360: the files the reader attached to the item, oldest first; absent where they attached
   * none, and from an older server. Only their own.
   */
  files?: IGuestOwnFile[];
}

/** I-2360: one file the reader attached to a shared item, as they read it back. */
export interface IGuestOwnFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

/** F-0692 AGM-65: one shared version of an item's description, as a member reads it. */
export interface ISharedDescriptionVersion {
  version: number;
  content: string;
  sharedAt: string;
  sharedByName: string | null;
}

/** F-0692 AGM-67: a version a guest may switch to; `version` null is what they filed. */
export interface IGuestDescriptionVersion {
  version: number | null;
  sharedAt: string | null;
  content: string;
}

/** A question put to the guest's role: its prompt and options, and their answer once given. */
export interface IGuestQuestion {
  id: string;
  prompt: string;
  /** PX-22: an option may show a picture of the item, drawn through `pictureAddress`. */
  options: Array<{ key: string; label: string; pictureAttachmentId?: string }>;
  selectionMode: 'single' | 'multiple' | string;
  allowOther: boolean;
  /** PX-22: the answer must say why. Absent from an older server. */
  asksWhy?: boolean;
  /** `note` is the why given; absent from an older server. */
  answer: { optionKeys: string[]; otherText: string | null; note?: string | null } | null;
}

/** What the list sends beside each body, keyed by item id, to group it and offer the sign-off. */
interface IGuestItemContext {
  workAreaId: string;
  workAreaName: string;
  sharedWith: GuestRoleKeyType[];
  updatedAt: string;
  /** The approvals waiting for this guest's sign-off, each with the answers it takes (F-0270 RL-6a). */
  awaiting?: ISharedItemAwaiting[];
  /**
   * UXA-1: the page opened for the item's type that the guest is shown (a Job's page), or none. The
   * server resolves it, so the item's type never reaches the guest. Absent from an older server.
   */
  itemPageId?: string | null;
  /** I-2235 (CRQ-7): the reader sent it, so they may comment on it. Absent from an older server. */
  sentByYou?: boolean;
}

/** One item shared with a guest, as the page holds it: the body joined with its list context. */
export type ISharedItem = IGuestItemBody & IGuestItemContext;

export interface ISharedItemAwaiting {
  /** I-1950: the request's handle, which the sign-off names; a guest never reads the flow's node. */
  requestId: string;
  outputs: string[];
  /** The guest role the sign-off is asked in: `tester`, `customer` or `partner`. */
  role: string;
  /** Which output approves and which declines, as the approval rule names them. */
  approve: string;
  decline: string;
  /**
   * F-0621 (AGM-34): the flow's words for the outputs this request takes; absent when it words none,
   * and from an older server.
   */
  outputLabels?: Array<{ output: string; labelKey: string }>;
  /**
   * F-0621 AGM-60: a current revision set governs this request, so it is answered by approving the
   * set by its digest, not by a sign-off.
   */
  revision?: boolean;
  /**
   * PX-13: the request asks the guest to agree to a plan (what will be made, the price, the date),
   * answered on the item's own page with the values shown there, never by a sign-off. Absent for
   * every other request, and from an older server.
   */
  agreement?: true;
}

/**
 * Where a customer moment's link or notice opens (I-2298, PX-15 d): the item, and its own page the
 * reader is shown, such as a Job's, where everything waiting in it is answered (UXA-1).
 */
export interface ICustomerLinkTarget {
  workAreaId: string;
  key: string;
  kind: string;
  itemPageId?: string | null;
}

/**
 * F-0271 RL-5b and RL-5c: which guest roles read an item, and what a guest reads. The server's one
 * row-level check answers every read here.
 */
class SharingApiService extends ApiService {
  async sharesOf(itemId: string): Promise<{ sharedWith: GuestRoleKeyType[] }> {
    const what = "The item's guest shares";
    const answer = expectRecord<{ sharedWith: GuestRoleKeyType[] }>(
      await this.get(`/work-items/${encodeURIComponent(itemId)}/shares`),
      what,
      ['sharedWith'],
    );
    expectList(answer.sharedWith, what);
    return answer;
  }

  /** Exactly these roles read the item afterwards; an empty list shares it with none. */
  share(
    itemId: string,
    roles: readonly GuestRoleKeyType[],
  ): Promise<{ sharedWith: GuestRoleKeyType[] }> {
    return this.put(`/work-items/${encodeURIComponent(itemId)}/shares`, { roles });
  }

  /** F-0692 AGM-65: the description's shared versions, and whether the guests read it as it is now. */
  async sharedDescriptions(
    itemId: string,
  ): Promise<{ versions: ISharedDescriptionVersion[]; currentIsShared: boolean }> {
    const what = "The description's shared versions";
    const answer = expectRecord<{
      versions: ISharedDescriptionVersion[];
      currentIsShared: boolean;
    }>(await this.get(`/work-items/${encodeURIComponent(itemId)}/shared-descriptions`), what, [
      'versions',
      'currentIsShared',
    ]);
    expectList(answer.versions, what);
    return answer;
  }

  /** F-0692 AGM-65: share the description as it stands as its next version. */
  shareDescription(itemId: string): Promise<{ version: ISharedDescriptionVersion }> {
    return this.post(`/work-items/${encodeURIComponent(itemId)}/shared-descriptions`, {});
  }

  /** F-0692 AGM-67: the versions of a shared item's description this guest may switch to. */
  async guestDescriptions(
    workAreaId: string,
    key: string,
  ): Promise<{ versions: IGuestDescriptionVersion[] }> {
    const what = 'The versions of the description';
    const answer = expectRecord<{ versions: IGuestDescriptionVersion[] }>(
      await this.get(
        `/shared-items/${encodeURIComponent(workAreaId)}/${encodeURIComponent(key)}/descriptions`,
      ),
      what,
      ['versions'],
    );
    expectList(answer.versions, what);
    return answer;
  }

  async sharedWithMe(): Promise<{ items: ISharedItem[] }> {
    const what = 'The items shared with you';
    const answer = expectRecord<{
      items: IGuestItemBody[];
      context: Record<string, IGuestItemContext>;
    }>(await this.get('/shared-items'), what, ['items', 'context']);
    expectList(answer.items, what);
    expectRecord(answer.context, what, []);
    return {
      items: answer.items.flatMap((item) =>
        answer.context[item.id] ? [{ ...item, ...answer.context[item.id] }] : [],
      ),
    };
  }

  /** One item shared with the guest, by its key: the allowlisted body (T-0786, AGM-19) and nothing else. */
  async sharedItem(workAreaId: string, key: string): Promise<IGuestItemBody> {
    const what = 'The shared item';
    const answer = expectRecord<IGuestItemBody>(
      await this.get(`/shared-items/${encodeURIComponent(workAreaId)}/${encodeURIComponent(key)}`),
      what,
      ['id', 'key', 'title', 'messages', 'questions'],
    );
    expectList(answer.messages, what);
    expectList(answer.questions, what);
    return answer;
  }

  /**
   * I-2298: the item a customer moment's link opens, for the signed-in person it was mailed to.
   * Every link that does not open answers one 404 (`customer_link_unavailable`).
   */
  /** PX-15 d: the caller's own customer notice, opened by its id (`/-/open?notice=<id>`). */
  async openCustomerNotice(notificationId: string): Promise<ICustomerLinkTarget> {
    return expectRecord<ICustomerLinkTarget>(
      await this.post('/customer-links/open-notice', { notificationId }),
      'The notice',
      ['workAreaId', 'key'],
    );
  }

  /** I-2461 (PX-14b): the moment a sign-in link was asked from, by its id (`/-/open?moment=<id>`). */
  async openCustomerMoment(momentId: string): Promise<ICustomerLinkTarget> {
    return expectRecord<ICustomerLinkTarget>(
      await this.post('/customer-links/open-moment', { momentId }),
      'The link',
      ['workAreaId', 'key'],
    );
  }

  async openCustomerLink(token: string): Promise<ICustomerLinkTarget> {
    return expectRecord<ICustomerLinkTarget>(
      await this.post('/customer-links/open', { token }),
      'The link',
      ['workAreaId', 'key'],
    );
  }

  /** The guest's sign-off: their answer to the approval waiting for them (RL-6a). */
  answer(
    item: Pick<ISharedItem, 'workAreaId' | 'key'>,
    answer: { requestId: string; output: string; note?: string | null },
  ): Promise<IGuestItemBody> {
    return this.post(
      `/shared-items/${encodeURIComponent(item.workAreaId)}/${encodeURIComponent(item.key)}/answer`,
      answer,
    );
  }

  /** AGM-61: what the guest fills in to file an item of this type; a 404 when they may not. */
  async filingForm(
    workAreaId: string,
    itemTypeKey: string,
    /** F-0779 PX-52: the reader's language, for the guide as the hint (`clarity.hint`). */
    language?: string,
  ): Promise<IGuestFilingForm> {
    const what = 'The form';
    const answer = expectRecord<IGuestFilingForm>(
      await this.get(
        `/shared-items/${encodeURIComponent(workAreaId)}/filing-forms/${encodeURIComponent(itemTypeKey)}`,
        language ? { params: { language } } : undefined,
      ),
      what,
      ['itemTypeKey', 'name', 'fields'],
    );
    expectList(answer.fields, what);
    return answer;
  }

  /**
   * PX-21 (§16.1 step 9): the filing form, prefilled from an item the guest reads, for a new item
   * like it; a 404 when they may not start one from it.
   */
  async requestAgain(workAreaId: string, key: string): Promise<IGuestFilingPrefill> {
    const what = 'The request form';
    const answer = expectRecord<IGuestFilingPrefill>(
      await this.get(
        `/shared-items/${encodeURIComponent(workAreaId)}/${encodeURIComponent(key)}/request-again`,
      ),
      what,
      ['form', 'startsFrom', 'title', 'content', 'values'],
    );
    expectList(answer.form.fields, what);
    return answer;
  }

  /**
   * PX-35 (I-2384): the title written from the message the guest is typing, while the work area
   * writes titles; `null` when nothing was written, and the title stays theirs.
   */
  async writeFilingTitle(
    workAreaId: string,
    itemTypeKey: string,
    message: string,
  ): Promise<string | null> {
    const answer = (await this.post(
      `/shared-items/${encodeURIComponent(workAreaId)}/filing-forms/${encodeURIComponent(itemTypeKey)}/title`,
      { message },
    )) as { title?: unknown } | null;
    return typeof answer?.title === 'string' && answer.title.trim() ? answer.title : null;
  }

  /**
   * F-0779 (§24.6, PX-46, PX-57): one step of the clarity check on the guest's form: the questions
   * (`ask`) or the request restated (`brief`). Stateless: the exchange so far goes with each step.
   */
  clarity(
    workAreaId: string,
    itemTypeKey: string,
    request: IClarityStepRequest,
  ): Promise<IClarityAnswer> {
    return this.post(
      `/shared-items/${encodeURIComponent(workAreaId)}/filing-forms/${encodeURIComponent(itemTypeKey)}/clarity`,
      request,
      // F-0785: the brief may take the server 40 s, past the client's default 20.
      { timeout: clarityCallerWaitMs(request.step) },
    );
  }

  /** AGM-61: the guest files an item of a type that names their role; its allowlisted body back. */
  file(
    workAreaId: string,
    filing: {
      itemTypeKey: string;
      title: string;
      content: string;
      values: Record<string, unknown>;
      /** PX-21: the key of the item this one starts from; the new item names it. */
      startsFrom?: string;
      /** F-0779 PX-51: the original words and the clarity exchange, kept with the item. */
      clarity?: IClarityFiling;
    },
    /**
     * F-0710 CRQ-2 (D-0398 a): the files sent with the filing, each with the id the text names it by
     * while it is only on the writer's device (an inline picture), or null. With files the filing is
     * one multipart request, so nothing is filed without them; without, it is the JSON it was.
     */
    files: ReadonlyArray<{ file: File; pendingId: string | null }> = [],
    onProgress?: (fraction: number) => void,
  ): Promise<IGuestItemBody> {
    const url = `/shared-items/${encodeURIComponent(workAreaId)}`;
    if (files.length === 0) return this.post(url, filing);
    const form = new FormData();
    form.append('itemTypeKey', filing.itemTypeKey);
    form.append('title', filing.title);
    form.append('content', filing.content);
    form.append('values', JSON.stringify(filing.values));
    form.append('fileIds', JSON.stringify(files.map((one) => one.pendingId)));
    if (filing.startsFrom) form.append('startsFrom', filing.startsFrom);
    // F-0779 PX-51: beside the files, `clarity` travels as JSON text, as `values` does.
    if (filing.clarity) form.append('clarity', JSON.stringify(filing.clarity));
    files.forEach((one) => form.append('files', one.file));
    return this.post(url, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (event.total) onProgress?.(Math.min(1, event.loaded / event.total));
      },
    });
  }

  /** D-0398 a: files added to an item the guest filed, while it is shared with them. */
  addFiles(
    item: Pick<ISharedItem, 'workAreaId' | 'key'>,
    files: readonly File[],
  ): Promise<Array<{ id: string; filename: string; mimeType: string; size: number }>> {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    return this.post(
      `/shared-items/${encodeURIComponent(item.workAreaId)}/${encodeURIComponent(item.key)}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  }

  /** D-0397 b: the short-lived address this guest draws one inline picture of a shared item from. */
  pictureAddress(
    item: Pick<ISharedItem, 'workAreaId' | 'key'>,
    attachmentId: string,
  ): Promise<{
    url: string;
    thumbnailUrl?: string;
    screenUrl?: string;
    mimeType?: string;
    expiresAt: string;
  }> {
    return this.get(
      `/shared-items/${encodeURIComponent(item.workAreaId)}/${encodeURIComponent(item.key)}/attachments/${encodeURIComponent(attachmentId)}/view-url`,
    );
  }

  /** AGM-19: the guest's answer to a question put to their role on this item. */
  answerQuestion(
    item: Pick<ISharedItem, 'workAreaId' | 'key'>,
    questionId: string,
    answer: { optionKeys: string[]; otherText?: string | null },
  ): Promise<IGuestItemBody> {
    return this.post(
      `/shared-items/${encodeURIComponent(item.workAreaId)}/${encodeURIComponent(item.key)}/questions/${encodeURIComponent(questionId)}/answer`,
      answer,
    );
  }

  /** PX-4: the guest's answers to the questions waiting on one item, sent together; all or none land. */
  answerQuestions(
    item: Pick<ISharedItem, 'workAreaId' | 'key'>,
    body: IGuestQuestionAnswersInput,
  ): Promise<IGuestItemBody> {
    return this.post(
      `/shared-items/${encodeURIComponent(item.workAreaId)}/${encodeURIComponent(item.key)}/questions/answers`,
      body,
    );
  }
}

export const sharingApi = new SharingApiService();
/**
 * The sharing API for the connection that holds `workAreaId` (I-1120): a work area bound to a custom
 * connection is asked through that connection's transport; any other is `sharingApi`.
 */
export function sharingApiFor(workAreaId: string | null | undefined): SharingApiService {
  return workAreaApis.api(
    workAreaId,
    'sharingApi',
    (clients) => new SharingApiService(clients.transport),
    sharingApi,
  );
}
